// client/src/screens/admin/products/textile/TextilesFormContent.js

import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Uploader from "../../../../components/images/Uploader";
import CreateSubcategoryModal from "../../../../components/modals/CreateSubcategoryModal";
import CreateCategoryModal from "../../../../components/modals/CreateCategoryModal";

import { adminListCategories } from "../../../../actions/categoryActions";
import { adminListSubcategories } from "../../../../actions/subcategoryActions";

import {
  Field,
  FLAG_FIELDS,
  Section,
  SHOP_KEY,
  TEXTILE_VARIANT_OPTIONS,
  getIdValue,
  getVariantUnitValue,
  mergeById,
  normalizeSku,
  normalizeVariantType,
  safeText,
} from "./TextilesProductForm.shared.jsx";

/* -------------------------------------------------------------------------- */
/*                              Catalog section                               */
/* -------------------------------------------------------------------------- */

const TextileCatalogSection = memo(function TextileCatalogSection({
  shopKey = SHOP_KEY,
  category,
  subcategory,
  disabled = false,
  onCategoryChange,
  onSubcategoryChange,
}) {
  const dispatch = useDispatch();

  const categoryState = useSelector((state) => state.adminCategoryList || {});
  const subcategoryState = useSelector(
    (state) => state.adminSubcategoryList || {},
  );

  const [createdCategories, setCreatedCategories] = useState([]);
  const [createdSubcategories, setCreatedSubcategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);

  const categoryList = useMemo(
    () => categoryState.categories || categoryState.items || [],
    [categoryState.categories, categoryState.items],
  );
  const subcategoryList = useMemo(
    () => subcategoryState.subcategories || subcategoryState.items || [],
    [subcategoryState.items, subcategoryState.subcategories],
  );

  const loadingCategories = Boolean(categoryState.loading);
  const loadingSubcategories = Boolean(subcategoryState.loading);

  const categoriesError = safeText(categoryState.error);
  const subcategoriesError = safeText(subcategoryState.error);

  useEffect(() => {
    if (!shopKey) return;

    dispatch(adminListCategories({ shopKey }));
    dispatch(adminListSubcategories({ shopKey }));
  }, [dispatch, shopKey]);

  useEffect(() => {
    if (!category && subcategory) {
      onSubcategoryChange("");
    }
  }, [category, subcategory, onSubcategoryChange]);

  const categories = useMemo(
    () =>
      mergeById([...categoryList, ...createdCategories]).filter((item) => {
        const itemShopKey = safeText(item?.shopKey);
        return !shopKey || !itemShopKey || itemShopKey === shopKey;
      }),
    [categoryList, createdCategories, shopKey],
  );

  const subcategories = useMemo(
    () =>
      mergeById([...subcategoryList, ...createdSubcategories]).filter(
        (item) => {
          const itemShopKey = safeText(item?.shopKey);
          const parentId = getIdValue(item?.parentId);

          const sameShop = !shopKey || !itemShopKey || itemShopKey === shopKey;
          const sameParent = !category || parentId === category;

          return sameShop && sameParent;
        },
      ),
    [category, createdSubcategories, shopKey, subcategoryList],
  );

  const handleCreatedCategory = useCallback(
    (created) => {
      const id = getIdValue(created);
      if (!id) return;

      setCreatedCategories((prev) => mergeById([...prev, created]));
      onCategoryChange(id);
      onSubcategoryChange("");
      setShowCategoryModal(false);
    },
    [onCategoryChange, onSubcategoryChange],
  );

  const handleCreatedSubcategory = useCallback(
    (created) => {
      const id = getIdValue(created);
      if (!id) return;

      const parentId = getIdValue(created?.parentId);

      setCreatedSubcategories((prev) => mergeById([...prev, created]));

      if (parentId) {
        onCategoryChange(parentId);
      }

      onSubcategoryChange(id);
      setShowSubcategoryModal(false);
    },
    [onCategoryChange, onSubcategoryChange],
  );

  return (
    <>
      <Section
        title="Category Assignment"
        hint="Choose an existing category or create one without leaving the product form."
      >
        <div className="admin-form-grid admin-form-grid--2">
          <div className="admin-field">
            <div className="admin-field-row">
              <span className="admin-field__label">Category</span>

              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                onClick={() => setShowCategoryModal(true)}
                disabled={disabled || !shopKey}
              >
                + New category
              </button>
            </div>

            <select
              className="admin-select"
              value={category}
              onChange={(event) => {
                onCategoryChange(event.target.value);
                onSubcategoryChange("");
              }}
              disabled={disabled || !shopKey || loadingCategories}
            >
              <option value="">
                {!shopKey
                  ? "Choose shop first"
                  : loadingCategories
                    ? "Loading categories..."
                    : categories.length
                      ? "Select category"
                      : "No categories yet"}
              </option>

              {categories.map((item) => {
                const id = getIdValue(item);

                return (
                  <option key={id} value={id}>
                    {item.name}
                  </option>
                );
              })}
            </select>

            {categoriesError ? (
              <div className="admin-feedback admin-feedback--error">
                {categoriesError}
              </div>
            ) : null}
          </div>

          <div className="admin-field">
            <div className="admin-field-row">
              <span className="admin-field__label">Subcategory</span>

              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                onClick={() => setShowSubcategoryModal(true)}
                disabled={disabled || !shopKey || !category}
              >
                + New subcategory
              </button>
            </div>

            <select
              className="admin-select"
              value={subcategory}
              onChange={(event) => onSubcategoryChange(event.target.value)}
              disabled={disabled || !category || loadingSubcategories}
            >
              <option value="">
                {!category
                  ? "Choose category first"
                  : loadingSubcategories
                    ? "Loading subcategories..."
                    : subcategories.length
                      ? "Select subcategory"
                      : "No subcategories yet"}
              </option>

              {subcategories.map((item) => {
                const id = getIdValue(item);

                return (
                  <option key={id} value={id}>
                    {item.name}
                  </option>
                );
              })}
            </select>

            {subcategoriesError ? (
              <div className="admin-feedback admin-feedback--error">
                {subcategoriesError}
              </div>
            ) : null}
          </div>
        </div>
      </Section>

      <CreateCategoryModal
        show={showCategoryModal}
        onHide={() => setShowCategoryModal(false)}
        shopKey={shopKey}
        onCreated={handleCreatedCategory}
      />

      <CreateSubcategoryModal
        show={showSubcategoryModal}
        onHide={() => setShowSubcategoryModal(false)}
        shopKey={shopKey}
        parentId={category}
        categories={categories}
        onCreated={handleCreatedSubcategory}
      />
    </>
  );
});

/* -------------------------------------------------------------------------- */
/*                               Basic section                                */
/* -------------------------------------------------------------------------- */

const TextileBasicSection = memo(function TextileBasicSection({
  form,
  disabled,
  setField,
}) {
  return (
    <Section
      title="Basic Information"
      hint="Name, description, category, and SKU are required."
    >
      <div className="admin-form-grid admin-form-grid--3">
        <Field label="Fabric name">
          <input
            className="admin-input"
            value={form.name}
            placeholder="Fabric name"
            disabled={disabled}
            onChange={(event) => setField("name", event.target.value)}
          />
        </Field>

        <Field label="Brand">
          <input
            className="admin-input"
            value={form.brand}
            placeholder="Brand"
            disabled={disabled}
            onChange={(event) => setField("brand", event.target.value)}
          />
        </Field>

        <Field label="SKU" hint="Required. Normalized to uppercase.">
          <input
            className="admin-input"
            value={form.sku}
            placeholder="SKU"
            disabled={disabled}
            onChange={(event) => setField("sku", event.target.value)}
            onBlur={() => setField("sku", normalizeSku(form.sku))}
          />
        </Field>
      </div>

      <div className="admin-form-spacer">
        <Field label="Description">
          <textarea
            className="admin-textarea"
            value={form.description}
            placeholder="Description"
            rows={4}
            disabled={disabled}
            onChange={(event) => setField("description", event.target.value)}
          />
        </Field>
      </div>
    </Section>
  );
});

/* -------------------------------------------------------------------------- */
/*                              Selling section                               */
/* -------------------------------------------------------------------------- */

const TextileSellingModeToggle = memo(function TextileSellingModeToggle({
  isVariant,
  disabled,
  setField,
}) {
  return (
    <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
      <label className="admin-inline-check">
        <input
          type="radio"
          name="textile-selling-mode"
          checked={!isVariant}
          disabled={disabled}
          onChange={() => setField("isVariant", false)}
        />
        <span>Single buying option</span>
      </label>

      <label className="admin-inline-check">
        <input
          type="radio"
          name="textile-selling-mode"
          checked={isVariant}
          disabled={disabled}
          onChange={() => setField("isVariant", true)}
        />
        <span>Product with variants</span>
      </label>
    </div>
  );
});

const TextileSimpleSellingFields = memo(function TextileSimpleSellingFields({
  form,
  disabled,
  setField,
}) {
  return (
    <>
      <label><input type="checkbox" checked={form.enabled !== false && form.unitType !== "cut"} disabled={disabled || form.unitType === "cut"}
        onChange={(event) => setField("enabled", event.target.checked)} /> Enabled in Shop</label>
      <div className="admin-form-grid admin-form-grid--3">
        <Field label="Option type">
          <select
            className="admin-select"
            value={form.unitType}
            disabled={disabled}
            onChange={(event) => {
              const nextType = normalizeVariantType(event.target.value);

              setField("unitType", nextType);
              setField("unitValue", String(getVariantUnitValue(nextType)));
            }}
          >
            {TEXTILE_VARIANT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value} disabled={option.legacy}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Price">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            value={form.price}
            placeholder="Price"
            disabled={disabled}
            onChange={(event) => setField("price", event.target.value)}
          />
        </Field>

        <Field
          label="Unit value"
          hint="Sample, Half Metre and One Metre use fixed unit definitions."
        >
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.001"
            value={form.unitType === "cut" ? form.unitValue : getVariantUnitValue(form.unitType)}
            placeholder="e.g. 1, 0.5"
            disabled={disabled}
            readOnly
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
        <Field label="Length in stock">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.001"
            value={form.lengthInStock}
            disabled={disabled}
            onChange={(event) => setField("lengthInStock", event.target.value)}
          />
        </Field>

        <Field label="Length reserved">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.001"
            value={form.lengthReserved}
            disabled={disabled}
            onChange={(event) => setField("lengthReserved", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--3 admin-form-spacer">
        <Field label="Sample stock">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="1"
            value={form.sampleInStock}
            disabled={disabled}
            onChange={(event) => setField("sampleInStock", event.target.value)}
          />
        </Field>

        <Field label="Sample reserved">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="1"
            value={form.sampleReserved}
            disabled={disabled}
            onChange={(event) => setField("sampleReserved", event.target.value)}
          />
        </Field>

        <Field label="Sample limit">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="1"
            value={form.sampleLimit}
            disabled={disabled}
            onChange={(event) => setField("sampleLimit", event.target.value)}
          />
        </Field>
      </div>

      <p className="admin-form-section__hint admin-form-spacer">
        Length stock and sample stock are separate inventory pools. A simple
        textile can be sold as a sample while still tracking available fabric
        length.
      </p>
    </>
  );
});

const TextileVariantInventoryFields = memo(
  function TextileVariantInventoryFields({ form, disabled, setField }) {
    return (
      <>
        <div className="admin-form-grid admin-form-grid--3">
          <Field label="Length in stock">
            <input
              className="admin-input"
              type="number"
              min="0"
              step="0.001"
              value={form.lengthInStock}
              disabled={disabled}
              onChange={(event) =>
                setField("lengthInStock", event.target.value)
              }
            />
          </Field>

          <Field label="Length reserved">
            <input
              className="admin-input"
              type="number"
              min="0"
              step="0.001"
              value={form.lengthReserved}
              disabled={disabled}
              onChange={(event) =>
                setField("lengthReserved", event.target.value)
              }
            />
          </Field>

          <Field label="Sample stock">
            <input
              className="admin-input"
              type="number"
              min="0"
              step="1"
              value={form.sampleInStock}
              disabled={disabled}
              onChange={(event) =>
                setField("sampleInStock", event.target.value)
              }
            />
          </Field>
        </div>

        <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
          <Field label="Sample reserved">
            <input
              className="admin-input"
              type="number"
              min="0"
              step="1"
              value={form.sampleReserved}
              disabled={disabled}
              onChange={(event) =>
                setField("sampleReserved", event.target.value)
              }
            />
          </Field>

          <Field label="Sample limit">
            <input
              className="admin-input"
              type="number"
              min="0"
              step="1"
              value={form.sampleLimit}
              disabled={disabled}
              onChange={(event) => setField("sampleLimit", event.target.value)}
            />
          </Field>
        </div>
      </>
    );
  },
);

const TextileVariantOptionCard = memo(function TextileVariantOptionCard({
  variant,
  index,
  disabled,
  usedVariantTypes,
  setVariantField,
  removeVariant,
}) {
  return (
    <div className="admin-variant-card">
      <div className="admin-form-grid admin-form-grid--2">
        <Field label="Option">
          <select
            className="admin-select"
            value={variant.variantType}
            disabled={disabled}
            onChange={(event) =>
              setVariantField(
                index,
                "variantType",
                normalizeVariantType(event.target.value),
              )
            }
          >
            {TEXTILE_VARIANT_OPTIONS.map((option) => {
              const usedByAnother =
                option.value !== variant.variantType &&
                usedVariantTypes.has(option.value);

              return (
                <option
                  key={`${index}-${option.value}`}
                  value={option.value}
                  disabled={usedByAnother || option.legacy}
                >
                  {option.label}
                </option>
              );
            })}
          </select>
        </Field>

        <Field label="Price">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            value={variant.price}
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "price", event.target.value)
            }
          />
        </Field>
      </div>

      <div className="admin-form-actions admin-form-spacer">
        <label><input type="checkbox" checked={variant.enabled !== false && variant.variantType !== "cut"} disabled={disabled || variant.variantType === "cut"}
          onChange={(event) => setVariantField(index, "enabled", event.target.checked)} /> Enabled in Shop</label>
        <button
          type="button"
          className="admin-btn admin-btn--secondary"
          onClick={() => removeVariant(index)}
          disabled={disabled}
        >
          Remove option
        </button>
      </div>
    </div>
  );
});

const TextileVariantSellingFields = memo(function TextileVariantSellingFields({
  form,
  disabled,
  setField,
  setVariantField,
  addVariant,
  removeVariant,
}) {
  const usedVariantTypes = useMemo(
    () => new Set(form.variants.map((variant) => variant.variantType)),
    [form.variants],
  );

  const canAddVariant = TEXTILE_VARIANT_OPTIONS.some((option) => !option.legacy && !usedVariantTypes.has(option.value));

  return (
    <>
      <TextileVariantInventoryFields
        form={form}
        disabled={disabled}
        setField={setField}
      />

      <p className="admin-form-section__hint admin-form-spacer">
        Add the buying options available for this textile. Sample can be a
        selectable option while still using the separate sample stock pool.
      </p>

      {form.variants.map((variant, index) => (
        <TextileVariantOptionCard
          key={variant._clientId || `textile-variant-${index}`}
          variant={variant}
          index={index}
          disabled={disabled}
          usedVariantTypes={usedVariantTypes}
          setVariantField={setVariantField}
          removeVariant={removeVariant}
        />
      ))}

      <div className="admin-form-actions">
        <button
          type="button"
          className="admin-btn admin-btn--secondary"
          onClick={addVariant}
          disabled={disabled || !canAddVariant}
        >
          Add buying option
        </button>
      </div>
    </>
  );
});

const TextileSellingSetupSection = memo(function TextileSellingSetupSection({
  form,
  disabled,
  setField,
  setVariantField,
  addVariant,
  removeVariant,
}) {
  return (
    <Section
      title="Selling Setup"
      hint="Buying option controls how this textile is sold. Length stock and sample stock are tracked independently."
    >
      <TextileSellingModeToggle
        isVariant={form.isVariant}
        disabled={disabled}
        setField={setField}
      />

      {!form.isVariant ? (
        <TextileSimpleSellingFields
          form={form}
          disabled={disabled}
          setField={setField}
        />
      ) : (
        <TextileVariantSellingFields
          form={form}
          disabled={disabled}
          setField={setField}
          setVariantField={setVariantField}
          addVariant={addVariant}
          removeVariant={removeVariant}
        />
      )}
    </Section>
  );
});

/* -------------------------------------------------------------------------- */
/*                              Details section                               */
/* -------------------------------------------------------------------------- */

const TextileDetailsSection = memo(function TextileDetailsSection({
  form,
  disabled,
  setField,
}) {
  return (
    <Section
      title="Fabric Details"
      hint="These attributes power public filters and faceted search."
    >
      <div className="admin-form-grid admin-form-grid--2">
        <Field label="Colors" hint="Comma or new-line separated.">
          <textarea
            className="admin-textarea"
            value={form.colorsText}
            rows={3}
            disabled={disabled}
            placeholder="Blue, Cream"
            onChange={(event) => setField("colorsText", event.target.value)}
          />
        </Field>

        <Field label="Materials" hint="Comma or new-line separated.">
          <textarea
            className="admin-textarea"
            value={form.materialsText}
            rows={3}
            disabled={disabled}
            placeholder="Cotton, Linen"
            onChange={(event) => setField("materialsText", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-spacer">
        <Field label="Patterns" hint="Comma or new-line separated.">
          <textarea
            className="admin-textarea"
            value={form.patternsText}
            rows={3}
            disabled={disabled}
            placeholder="Floral, Striped"
            onChange={(event) => setField("patternsText", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
        <Field label="Tags">
          <textarea
            className="admin-textarea"
            value={form.tagsText}
            rows={2}
            disabled={disabled}
            placeholder="summer, breathable"
            onChange={(event) => setField("tagsText", event.target.value)}
          />
        </Field>

        <Field label="Groups">
          <textarea
            className="admin-textarea"
            value={form.groupsText}
            rows={2}
            disabled={disabled}
            placeholder="new season, bridal"
            onChange={(event) => setField("groupsText", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
        <Field label="Origin">
          <input
            className="admin-input"
            value={form.origin}
            disabled={disabled}
            placeholder="Origin"
            onChange={(event) => setField("origin", event.target.value)}
          />
        </Field>

        <Field label="Usage">
          <input
            className="admin-input"
            value={form.usage}
            disabled={disabled}
            placeholder="Usage"
            onChange={(event) => setField("usage", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-spacer">
        <Field label="Care instructions">
          <textarea
            className="admin-textarea"
            value={form.careInstructions}
            rows={3}
            disabled={disabled}
            placeholder="Care instructions"
            onChange={(event) =>
              setField("careInstructions", event.target.value)
            }
          />
        </Field>
      </div>
    </Section>
  );
});

/* -------------------------------------------------------------------------- */
/*                              Images / flags                                */
/* -------------------------------------------------------------------------- */

const TextileImagesSection = memo(function TextileImagesSection({
  images,
  disabled,
  setImages,
}) {
  return (
    <Section
      title="Product Images"
      hint="The first image is used as the cover image."
    >
      <Uploader
        existingImages={images}
        setImageUrls={setImages}
        folder="products"
        disabled={disabled}
      />
    </Section>
  );
});

const TextileMadeToMeasureSection = memo(function TextileMadeToMeasureSection({
  form,
  disabled,
  setField,
}) {
  return (
    <Section
      title="Made To Measure"
      hint="Only staff-approved textiles appear in the guided made-to-measure fabric step."
    >
      <label className="admin-inline-check">
        <input
          type="checkbox"
          checked={Boolean(form.madeToMeasureEligible)}
          disabled={disabled}
          onChange={(event) =>
            setField("madeToMeasureEligible", event.target.checked)
          }
        />
        <span>Available as a made-to-measure textile</span>
      </label>

      <div className="admin-form-spacer">
        <Field label="Suitability notes">
          <textarea
            className="admin-textarea"
            value={form.madeToMeasureSuitabilityNotes}
            rows={3}
            disabled={disabled}
            placeholder="Drape, weight, care, or construction notes for MTM."
            onChange={(event) =>
              setField("madeToMeasureSuitabilityNotes", event.target.value)
            }
          />
        </Field>
      </div>
    </Section>
  );
});

const TextileFlagsSection = memo(function TextileFlagsSection({
  form,
  disabled,
  setField,
}) {
  return (
    <Section
      title="Visibility & Flags"
      hint="These content flags are editable on create and edit. Publishing and draft state are handled from the product list."
    >
      <div className="admin-flags-grid">
        {FLAG_FIELDS.map((key) => (
          <label key={key} className="admin-inline-check">
            <input
              type="checkbox"
              checked={Boolean(form[key])}
              disabled={disabled}
              onChange={(event) => setField(key, event.target.checked)}
            />
            <span>{key}</span>
          </label>
        ))}
      </div>
    </Section>
  );
});

/* -------------------------------------------------------------------------- */
/*                                  Content                                   */
/* -------------------------------------------------------------------------- */

const TextilesFormContent = memo(function TextilesFormContent({
  form,
  shopKey,
  submitDisabled,
  setField,
  setVariantField,
  setImages,
  addVariant,
  removeVariant,
}) {
  const handleCategoryChange = useCallback(
    (value) => setField("category", value),
    [setField],
  );

  const handleSubcategoryChange = useCallback(
    (value) => setField("subcategory", value),
    [setField],
  );

  return (
    <>
      <TextileCatalogSection
        shopKey={shopKey}
        category={form.category}
        subcategory={form.subcategory}
        disabled={submitDisabled}
        onCategoryChange={handleCategoryChange}
        onSubcategoryChange={handleSubcategoryChange}
      />

      <TextileBasicSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
      />

      <TextileSellingSetupSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
        setVariantField={setVariantField}
        addVariant={addVariant}
        removeVariant={removeVariant}
      />

      <TextileDetailsSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
      />

      <TextileImagesSection
        images={form.images}
        disabled={submitDisabled}
        setImages={setImages}
      />

      <TextileMadeToMeasureSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
      />

      <TextileFlagsSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
      />
    </>
  );
});

export default TextilesFormContent;
